import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { CellContext, ColumnDef, HeaderContext } from "@tanstack/react-table";
import type React from "react";
import { useLayoutEffect, useRef } from "react";

import type { DispatchCoreKind, DispatchEntity } from "@storyboard/dispatch-view/fieldKinds";
import {
  CORE_FIELD_KINDS,
  DISPATCH_CORE_KINDS,
  enumValueFor,
  fieldEntity,
  fieldHasRole,
  fieldKindFor,
  fieldRoles,
  isDispatchCoreKind,
} from "@storyboard/dispatch-view/fieldKinds";
import type {
  DispatchColor,
  DispatchColumn,
  DispatchField,
  DispatchGrain,
  DispatchViewDefinition,
} from "@storyboard/dispatch-view/view";
import { parseFieldKey } from "@storyboard/driver-query/predicate";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { type DispatchTableRow, rowMessageIsPhoto, rowUnacknowledged } from "@/hooks/dispatch/dispatchTableRow";
import { dispatchCoreKey, dispatchTagKey } from "@/hooks/dispatch/projectDispatchRecord";
import type { TagDefinition } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import { CoordinatesLink } from "@/ui/CoordinatesLink";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import { TagChipCell } from "@/ui/TagChipCell";
import { zinc } from "@/ui/tokens";
import { redColor } from "@/utils/color";
import { LOCATION_TOOLTIP_ENTER_DELAY, locationProvenanceLabel, locationStaleness } from "@/utils/locationStaleness";
import { locationText } from "@/utils/locationText";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";
import { dispatchColorFor } from "./dispatchColors";
import { BooleanMark, EnumPill, RelativeTime } from "./FieldValue";
import { formatVolume } from "./format";
import { NextStopCell } from "./NextStopCell";
import { HighlightedText, markMatches, SEARCH_MARK_SX, useSearchTermFor } from "./searchHighlight";
import { TextCell } from "./TextCell";

export { DISPATCH_COLOR_VALUES, dispatchColorFor } from "./dispatchColors";

/** Default width in px for a core kind's column before the user resizes it. */
const CORE_KIND_WIDTHS: Partial<Record<DispatchCoreKind, number>> = {
  latest_item_at: 176,
  item_at: 176,
  volume: 80,
  hands_free: 100,
  personal_device: 120,
  recent_message: 360,
  pta: 176,
  next_stop: 200,
};

const DEFAULT_COLUMN_WIDTH = 160;

/**
 * Whether a field may be a column at all: the registry gives it the `column` role. The acknowledgement layer (the dot
 * already shows it), the hidden kinds, and the integration tag have no such role; a view naming one of these keeps
 * validating, and the table leaves the column out.
 */
export function canBeColumn(field: DispatchField): boolean {
  return fieldHasRole(field, "column");
}

/** Whether a field may be a sort or a grouping: the registry gives it either role. */
export function canBeField(field: DispatchField): boolean {
  return fieldHasRole(field, "sort") || fieldHasRole(field, "group");
}

/** Whether a field is an accent field: never a column, yet offered wherever a sort, grouping, or condition is chosen. */
export function isAccentField(field: DispatchField): boolean {
  return canBeField(field) && !canBeColumn(field);
}

/** Which section of a field picker a field belongs under: the driver's fields, one message's, or the tags. */
export function fieldGroup(field: DispatchField): "Driver" | "Message" | "Tags" {
  if (parseFieldKey(field)?.strategy === "tag") return "Tags";
  return fieldEntity(field) === "message" ? "Message" : "Driver";
}

/** Whether a core field describes the given entity. */
function isEntity(kind: DispatchCoreKind, entity: DispatchEntity): boolean {
  return CORE_FIELD_KINDS[kind].entity === entity;
}

/** The structural column ids the table adds before the view's: the acknowledgement dot and the selection box. */
export const DOT_COLUMN_ID = "_dot";

/** Where a driver row's unacknowledged count saturates, read as `99+`. */
export const DRIVER_BADGE_CAP = 99;

/** Whether a column id is one of the table's own, not a field a member lays out. */
export function isStructuralColumnId(columnId: string): boolean {
  return columnId.startsWith("_");
}

/**
 * The leading cell of every row: red while the row waits on an acknowledgement, nothing otherwise. On a driver row the
 * red is a circle carrying how many of the driver's messages wait, `N+` at `badgeCap`, drawn from the store, which the
 * warmup fills with every channel's last two days; on a message row, which is about one message, it is a plain dot.
 * Always pinned first, never hidden, never in the Display lists. The dot is about handling, not reading: it goes only
 * when a dispatcher acknowledges.
 */
export function acknowledgementDotColumn(badgeCap: number): ColumnDef<DataTableFeatures, DispatchTableRow> {
  return {
    id: DOT_COLUMN_ID,
    header: () => null,
    cell: ({ row }) => {
      const waiting = rowUnacknowledged(row.original);
      const count = row.original.kind === "driver" ? row.original.unacknowledgedCount : 0;
      if (waiting && count > 0) {
        return (
          <Box
            data-sb={TestIds.dispatch.rowDot}
            aria-label={`${count} unacknowledged`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: 9,
              bgcolor: redColor,
              color: "white",
              fontSize: "0.6875rem",
              fontWeight: 600,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
              mx: "auto",
            }}
          >
            {count >= badgeCap ? `${badgeCap}+` : count}
          </Box>
        );
      }
      return (
        <Box
          data-sb={waiting ? TestIds.dispatch.rowDot : undefined}
          aria-label={waiting ? "Unacknowledged" : undefined}
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: waiting ? redColor : "transparent", mx: "auto" }}
        />
      );
    },
    meta: { flex: 0, compact: true, frozen: true, alwaysPinned: true },
    size: 32,
    minSize: 32,
    maxSize: 32,
    enableSorting: false,
    enableGlobalFilter: false,
    enableResizing: false,
    enableHiding: false,
  };
}

/** Pixel budget so a header stays readable at first paint: cell padding, the drag grabber, and the title. */
function initialMinWidth(label: string): number {
  return 16 + 20 + Math.ceil(label.length * 10) + 20;
}

/** The label a column shows: the view's override, else the tag's label or the core kind's default. */
export function dispatchColumnLabel(column: DispatchColumn, tagsBySlug: ReadonlyMap<string, TagDefinition>): string {
  if (column.label) return column.label;
  return fieldLabel(column.field, tagsBySlug);
}

/** The label of a field on its own: the registry's for a core kind, the tag's label for a tag. */
export function fieldLabel(field: DispatchField, tagsBySlug: ReadonlyMap<string, TagDefinition>): string {
  return fieldKindFor(field, tagsBySlug).label;
}

/**
 * The header text for any column the table offers: the view's override when it defines the column, else the field's.
 *
 * A column id is the field it shows, so a structural id (`_dot`) reads as itself.
 */
export function labelForColumnId(
  columnId: string,
  view: DispatchViewDefinition,
  tagsBySlug: ReadonlyMap<string, TagDefinition>,
): string {
  const defined = view.layout.columns.find((column) => column.field === columnId);
  return defined ? dispatchColumnLabel(defined, tagsBySlug) : fieldLabel(columnId, tagsBySlug);
}

function tagsByLabel(tags: readonly TagDefinition[]): TagDefinition[] {
  return tags
    .filter((tag) => fieldRoles(dispatchTagKey(tag.slug)).length > 0)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * Every column the table can offer: the view's columns in order, then the remaining driver fields, then the
 * workspace's tags by label. The view's columns are the ones shown until the member changes visibility. Left out
 * wherever they come from: the fields that are never a column (see {@link canBeColumn}) and the integration tag; the
 * message fields belong to a message-grain view's own column list rather than to a member adding one to a driver
 * view. This is the offered set `resolveDispatchView` lays the member's layout over.
 */
export function allDispatchColumns(
  view: DispatchViewDefinition,
  tags: readonly TagDefinition[],
  tagsBySlug: ReadonlyMap<string, TagDefinition>,
): DispatchColumn[] {
  const shown = view.layout.columns.filter((column) => canBeColumn(column.field));
  const defined = new Set(shown.map((column) => column.field));
  const extraCore: DispatchColumn[] = DISPATCH_CORE_KINDS.filter((kind) => isEntity(kind, "driver"))
    .map((kind) => ({ field: dispatchCoreKey(kind) }))
    .filter((column) => canBeColumn(column.field) && !defined.has(column.field));
  const extraTags: DispatchColumn[] = tagsByLabel(tags)
    .filter((tag) => tagsBySlug.has(tag.slug))
    .map((tag) => ({ field: dispatchTagKey(tag.slug) }))
    .filter((column) => !defined.has(column.field));
  return [...shown, ...extraCore, ...extraTags];
}

/**
 * Every field the rows can be sorted or grouped by, column or not, shown or not: the view's columns in order, then the
 * other driver fields and the acknowledgement layer, the message fields when a row is a message, then the workspace's
 * tags by label. The hidden kinds and the integration tag never appear; `item_unacknowledged` carries no role because
 * on a message row `unacknowledged` already means the message, and two entries reading "Unacknowledged" would only
 * puzzle.
 */
export function dispatchFieldCandidates(
  view: DispatchViewDefinition,
  tags: readonly TagDefinition[],
  grain: DispatchGrain,
): DispatchField[] {
  const seen = new Set<DispatchField>();
  const add = (field: DispatchField) => {
    if (!seen.has(field) && canBeField(field)) seen.add(field);
  };
  for (const column of view.layout.columns) add(column.field);
  for (const kind of DISPATCH_CORE_KINDS) {
    if (isEntity(kind, "message") && grain !== "message") continue;
    add(dispatchCoreKey(kind));
  }
  for (const tag of tagsByLabel(tags)) add(dispatchTagKey(tag.slug));
  return [...seen];
}

/**
 * A column's header: its label, a filter mark while an ad-hoc condition narrows on it, and the sort arrow when the
 * rows sort by it. The whole cell is the button that opens the column menu, so the header itself is presentational.
 */
function DispatchColumnHeader({
  label,
  sorted,
  filtered,
}: {
  label: string;
  sorted: false | "asc" | "desc";
  filtered: boolean;
}) {
  const marks =
    filtered || sorted ? (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: zinc[400], ml: 0.5 }}>
        {filtered && (
          <FilterAltIcon aria-label="Filtered" sx={{ fontSize: 14, color: "var(--color-brand-primary-400)" }} />
        )}
        {sorted === "asc" && <ArrowUpwardIcon aria-label="Sorted ascending" sx={{ fontSize: 14 }} />}
        {sorted === "desc" && <ArrowDownwardIcon aria-label="Sorted descending" sx={{ fontSize: 14 }} />}
      </Box>
    ) : undefined;
  return <ColumnHeader title={label} extra={marks} />;
}

/**
 * A row's message: the driver's words as sanitized HTML with a full-text tooltip, or, when the row describes what the
 * driver did (`described`, a photo), that description in italics so it never reads as the driver's own text. In a
 * column the search scans, the runs it matched are marked in the words shown.
 */
function MessageCell({ value, described, field }: { value: string; described: boolean; field: string }) {
  const term = useSearchTermFor(field);
  const html = value && !described ? sanitizeHtmlForDisplay(value) : "";
  const wordsRef = useRef<HTMLDivElement>(null);
  // The words are HTML React does not own below the box, so marking rewrites them from the clean HTML each time;
  // a box never marked and with nothing to mark is left alone.
  useLayoutEffect(() => {
    const words = wordsRef.current;
    if (!words || (term === "" && words.dataset.marked == null)) return;
    words.innerHTML = html;
    markMatches(words, term);
    if (term === "") delete words.dataset.marked;
    else words.dataset.marked = "";
  }, [html, term]);
  if (!value) return null;
  if (described) {
    return (
      <Box
        component="span"
        sx={{
          color: zinc[400],
          fontStyle: "italic",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...SEARCH_MARK_SX,
        }}
      >
        <HighlightedText text={value} term={term} />
      </Box>
    );
  }
  return (
    <Tooltip
      title={
        <Box
          className="display-transcript"
          dangerouslySetInnerHTML={{ __html: html }}
          sx={{ whiteSpace: "pre-wrap" }}
        />
      }
      enterDelay={1500}
      enterNextDelay={1500}
      placement="bottom-start"
      slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
    >
      <Box
        ref={wordsRef}
        className="display-transcript"
        component="div"
        dangerouslySetInnerHTML={{ __html: html }}
        sx={{ color: zinc[300], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...SEARCH_MARK_SX }}
      />
    </Tooltip>
  );
}

/**
 * Renders one cell by its field's kind: enum pills, boolean marks, relative timestamps, numbers, and text, with three
 * core kinds keeping a presentation of their own (the message with its tooltip, the volume percent, the next stop with
 * its stop-type marker).
 */
function coreCell(
  kind: DispatchCoreKind,
  column: DispatchColumn,
  info: CellContext<DataTableFeatures, DispatchTableRow, unknown>,
  now: number,
) {
  const value = String(info.getValue() ?? "");
  const paint = dispatchColorFor(column, value);
  const fieldKind = CORE_FIELD_KINDS[kind];
  switch (kind) {
    case "recent_message":
    case "item_text":
      return <MessageCell value={value} described={rowMessageIsPhoto(info.row.original)} field={column.field} />;
    case "volume":
      return <TextCell value={formatVolume(info.row.original.driver.volume)} />;
    case "next_stop":
      return (
        <NextStopCell
          value={value}
          role={info.row.original.driver.route?.nextStop?.role ?? null}
          field={column.field}
        />
      );
    case "location":
    case "state_province":
    case "country": {
      const driver = info.row.original.driver;
      // The location's coordinates fallback links to the map; its text is already the projected value.
      const text =
        kind === "location" && locationText(driver)?.coordinates ? (
          <CoordinatesLink latitude={driver.latitude} longitude={driver.longitude}>
            <TextCell value={value} field={column.field} />
          </CoordinatesLink>
        ) : (
          <TextCell value={value} field={column.field} />
        );
      if (!value) return text;
      const staleness = locationStaleness(driver.locationUpdatedAt, now);
      // The grey sits on the tooltip's anchor, a direct child of `Painted`, so a painted column's color still wins.
      return (
        <Tooltip
          title={locationProvenanceLabel(staleness, driver.locationSource)}
          enterDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
          enterNextDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
        >
          <Box component="span" sx={staleness.stale ? { color: zinc[600] } : undefined}>
            {text}
          </Box>
        </Tooltip>
      );
    }
    default:
      break;
  }
  switch (fieldKind.valueType) {
    case "enum":
      return <EnumPill value={value} known={enumValueFor(fieldKind, value)} paint={paint} />;
    case "boolean":
      return <BooleanMark value={value === "true"} paint={paint} />;
    case "timestamp":
      return value ? <RelativeTime iso={value} now={now} /> : null;
    default:
      return <TextCell value={value} field={column.field} />;
  }
}

/** Wraps a cell's content in the column's paint for that value; children inherit it over their own muted tones. */
function Painted({ color, children }: { color: string | undefined; children: React.ReactNode }) {
  if (!color) return <>{children}</>;
  return (
    <Box component="span" sx={{ color, display: "contents", "& > *": { color: "inherit" } }}>
      {children}
    </Box>
  );
}

function tagCell(column: DispatchColumn, values: string[]) {
  // A painted tag column reads as text in that color, like the other painted columns; unpainted tags keep their chips.
  if (column.color || column.valueColors) {
    return (
      <Painted color={dispatchColorFor(column, values[0] ?? "")}>
        <TextCell value={values.join(", ")} field={column.field} />
      </Painted>
    );
  }
  return <TagChipCell values={values} activeFilter={[]} />;
}

/**
 * Turns columns into TanStack column definitions over {@link DispatchTableRow}.
 *
 * Column ids are the field keys (`core:<kind>` / `tag:<slug>`), so grid state persisted by id survives a relabel.
 * Core kinds render like their driver-view counterparts, tag columns render the value list as chips, `pinned` maps to
 * `meta.frozen`, `width` to the initial size, and `color`/`valueColors` paint the values. Sorting is the page's, not
 * the table's: the header reads the sort from the table state to draw its arrow, and a click opens the column menu.
 *
 * @param columns - Usually {@link allDispatchColumns}: the view's columns plus every other field the member may show.
 * @param now - The page's minute clock, so relative timestamps age together.
 * @param filteredColumnIds - The columns an ad-hoc condition narrows on; their headers carry the filter mark.
 */
export function buildDispatchColumns(
  columns: readonly DispatchColumn[],
  tagsBySlug: ReadonlyMap<string, TagDefinition>,
  now: number,
  filteredColumnIds: ReadonlySet<string> = new Set(),
): ColumnDef<DataTableFeatures, DispatchTableRow>[] {
  return columns.map((column) => {
    const key = column.field;
    const ref = parseFieldKey(column.field);
    const label = dispatchColumnLabel(column, tagsBySlug);
    const pinned = column.pinned === true;
    const kind = ref?.strategy === "core" && isDispatchCoreKind(ref.kind) ? ref.kind : null;
    const size = column.width ?? (kind ? (CORE_KIND_WIDTHS[kind] ?? DEFAULT_COLUMN_WIDTH) : DEFAULT_COLUMN_WIDTH);
    const fieldKind = fieldKindFor(column.field, tagsBySlug);
    const centered = fieldKind.valueType === "boolean";
    return {
      id: key,
      accessorFn: (row) => row.record[key] ?? "",
      header: (context: HeaderContext<DataTableFeatures, DispatchTableRow, unknown>) => (
        <DispatchColumnHeader
          label={label}
          sorted={context.column.getIsSorted()}
          filtered={filteredColumnIds.has(key)}
        />
      ),
      cell: (info) => {
        if (ref?.strategy === "tag") {
          // A tag the workspace no longer defines projects to no key, and the accessor's fallback is a string.
          const raw = info.getValue();
          return tagCell(column, Array.isArray(raw) ? (raw as string[]) : []);
        }
        const value = String(info.getValue() ?? "");
        if (kind && (fieldKind.valueType === "enum" || fieldKind.valueType === "boolean")) {
          // Pills and marks carry their own color; painting over them would wash the registry color out.
          return coreCell(kind, column, info, now);
        }
        return (
          <Painted color={dispatchColorFor(column, value)}>
            {kind ? coreCell(kind, column, info, now) : <TextCell value={value} field={column.field} />}
          </Painted>
        );
      },
      size,
      minSize: 56,
      meta: {
        // `frozen` is only the default; the member's pinning override decides at render time, so every column stays
        // draggable and a column the view pinned can be unpinned and then moved like any other.
        frozen: pinned,
        draggable: true,
        flex: pinned || column.width != null ? undefined : 1,
        align: centered ? "center" : "left",
        initialMinWidth: Math.min(size, initialMinWidth(label)),
      },
      enableGlobalFilter: false,
      // A pinned column can still be hidden: pinning is about where it sits, not whether the member wants it.
      enableHiding: true,
    };
  });
}
